import { Box } from "@mui/material";
import { useRef, useState } from "react";
import MyErrorHelperText from "src/components/denseForm/ErrorHelperText";
import NewTextField from "src/components/form/TextField";

const CODE_LENGTH = 6;

export default function TwoFactorCodeInput({
  onChange,
  error,
}: {
  onChange?: (code: string) => void;
  error?: string;
}) {
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    // Only allow single digit 0-9
    if (!/^[0-9]{0,1}$/.test(value)) return;

    const newValues = [...values];
    newValues[index] = value; // This will override existing value
    setValues(newValues);
    onChange?.(newValues.join(""));

    // Auto-focus next input if a digit was entered
    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    // Move focus to previous input on backspace if current is empty
    if (e.key === "Backspace" && !values[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").replace(/\D/g, ""); // Remove non-digits

    if (pasteData.length === CODE_LENGTH) {
      const newValues = pasteData.split("");
      setValues(newValues);
      onChange?.(newValues.join(""));

      // Focus the last input
      inputsRef.current[CODE_LENGTH - 1]?.focus();
    }
  };

  return (
    <Box
      sx={{
        gap: 1,
      }}
    >
      <Box display="flex" gap={1} mb={2}>
        {values.map((digit, index) => (
          <NewTextField
            key={index}
            inputRef={(el) => (inputsRef.current[index] = el)}
            value={digit}
            autoComplete="one-time-code"
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            error={!!error}
            onFocus={(e) => e.target.select()}
            inputProps={{
              inputMode: "numeric",
              maxLength: 1,
              style: {
                textAlign: "center",
                fontSize: "1.5rem",
                width: "3rem",
                height: "3rem",
                padding: 0,
              },
            }}
            sx={{
              maxWidth: "56px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "secondary.main",
                },
                "&:hover fieldset": {
                  borderColor: "secondary.main",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "secondary.main",
                },
              },
            }}
            autoFocus={index === 0}
          />
        ))}
      </Box>
      <MyErrorHelperText error={!!error} helperText={error} />
    </Box>
  );
}
